import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import { getActorById, getDramaById } from "@/utils/content";

export default function Actor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const actor = getActorById(id);

  useEffect(() => {
    if (!actor) {
      document.title = "演员信息不存在 - 隔壁的女孩";
      return;
    }
    document.title = `${actor.name} - 参演作品介绍 | 隔壁的女孩`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        `${actor.name}演员资料：${actor.bio.slice(0, 60)} 查看其完整参演作品列表与角色信息。`,
      );
    }
  }, [actor]);

  if (!actor) {
    return (
      <div className="min-h-screen bg-background-100">
        <SiteHeader />
        <main className="pt-16 md:pt-[72px]">
          <div className="mx-auto flex max-w-2xl flex-col items-center px-4 py-24 text-center md:py-32">
            <span className="flex items-center justify-center w-16 h-16 rounded-full bg-background-200 text-foreground-400 mb-5">
              <i className="ri-user-unfollow-line text-3xl"></i>
            </span>
            <h1 className="font-heading text-2xl font-bold text-foreground-950">没有找到这位演员</h1>
            <p className="mt-3 text-sm text-foreground-500">演员资料可能已更新或链接有误，返回首页继续浏览吧。</p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-accent-600 transition-colors"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100">
      <SiteHeader />
      <main className="pt-16 md:pt-[72px]">
        <section className="border-b border-background-200 bg-background-50">
          <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-10">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "演员信息" }, { label: actor.name }]} />
            <div className="mt-6 flex flex-col items-center gap-6 sm:flex-row sm:items-end sm:gap-8">
              <div className="w-32 h-32 md:w-40 md:h-40 shrink-0 overflow-hidden rounded-full bg-background-200 ring-4 ring-background-100">
                <img
                  src={actor.avatar}
                  alt={`演员 ${actor.name}`}
                  title={`${actor.name} 参演作品介绍`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="min-w-0 flex-1 text-center sm:text-left">
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">{actor.name}</h1>
                <div className="mt-3 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                  {actor.tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-900">
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-foreground-600 sm:justify-start">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-cake-2-line text-accent-600"></i>
                    生日 {actor.birthday}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-map-pin-line text-accent-600"></i>
                    出生地 {actor.birthPlace}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-film-line text-accent-600"></i>
                    参演 {actor.works.length} 部
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="mt-6 inline-flex items-center gap-2 h-10 px-4 rounded-md border border-background-300 bg-background-50 text-sm font-medium text-foreground-800 whitespace-nowrap hover:border-accent-300 hover:text-accent-600 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回上一页
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-3xl">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">个人简介</h2>
            <p className="text-sm md:text-base leading-8 text-foreground-700">{actor.bio}</p>
          </div>
        </section>

        <section className="bg-background-200/60">
          <div className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">参演影视作品</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {actor.works.map((work) => {
                const drama = getDramaById(work.dramaId);
                return (
                  <Link
                    key={`${work.dramaId}-${work.role}`}
                    to={`/detail/${work.dramaId}`}
                    className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3.5 transition-colors hover:border-accent-300"
                  >
                    <div className="w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <div className="w-full aspect-[2/3]">
                        {drama && (
                          <img
                            src={drama.cover}
                            alt={`${work.title} 海报`}
                            title={`${work.title} 剧情介绍及演员信息`}
                            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                        )}
                      </div>
                    </div>
                    <div className="min-w-0 flex-1 py-1">
                      <span className="rounded bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">{work.year}</span>
                      <h3 className="mt-2 font-heading text-base font-semibold text-foreground-950 group-hover:text-accent-600 transition-colors">
                        {work.title}
                      </h3>
                      <p className="mt-1.5 text-sm text-foreground-600">{work.role}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}